import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, computed, ElementRef, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, output, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { Address } from '../../../interfaces/referralSections.interface';
import { NzAriaLabelDirective } from '../../directives/nz-aria-label.directive';
import { SectionsService } from '../../../services/sections.service';
import { CountyValidationService } from '../../services/county-validation.service';
import { LookupValueItem } from '../../../interfaces/lookup.interface';
import { LookupService } from '../../../services/lookup.service';
import * as SmartySDK from 'smartystreets-javascript-sdk';
import { environment } from '../../../../environments/environment';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { ScrollingModule } from '@angular/cdk/scrolling';

declare global {
  interface Window {
    dtrum?: any;
  }
}

const key = 'TA93-MT53-WE43-EC19';

@Component({
  selector: 'ppl-address',
  standalone: true,
  imports: [
    NzFormModule,
    NzInputModule,
    NzSelectModule,
    NzIconModule,
    FormsModule,
    ReactiveFormsModule,
    NzSwitchModule,
    CommonModule,
    NzAriaLabelDirective,
    NzToolTipModule,
    ScrollingModule
  ],
  templateUrl: './address.component.html',
  styleUrl: './address.component.css',
})
export class AddressComponent implements OnInit, OnChanges, OnDestroy {
  private countyValidationService = inject(CountyValidationService);
  statesListDropdown: LookupValueItem[] = [];
  @Input() addressForm!: FormGroup;
  @Input() mode = 'edit';
  @Input() data!: Address;
  @Input() is_permanent = true;
  @Input() isMandatory = true;
  @Input() showPSD = true;
  @Input() showFullAddress = true;
  @Output() onAddressSelection = new EventEmitter<any>();
  @ViewChild('manualToggle') manualToggle!: ElementRef<HTMLInputElement>;


  isParticipant: boolean = false;

  private _permanentValidationErrors = {};
  private credentials: any;
  private smartyAutoCompleteClient: any;
  private smartyStreetClient: any;
  private typingTimer: any;
  private isSmartyEnabled = false;

  @Input() set permanentValidationErrors(value) {
    this._permanentValidationErrors = value || {};
  }

  get permanentValidationErrors() {
    return this._permanentValidationErrors;
  }

  private _mailingValidationErrors = {};

  @Input() set mailingValidationErrors(value) {
    this._mailingValidationErrors = value || {};
  }

  get mailingValidationErrors() {
    return this._mailingValidationErrors;
  }

  private _serviceValidationErrors = {};

  @Input() set serviceValidationErrors(value) {
    this._serviceValidationErrors = value || {};
  }

  get serviceValidationErrors() {
    return this._serviceValidationErrors;
  }

  selectedValue = null;
  listOfOptions: { value: string; text: string }[] = [];
  nzFilterOptions = (): boolean => true;
  fullAddress = '';

  get isConsumerFlow(): boolean {
    return this.countyValidationService.isConsumerFlow
  }

  constructor(
    private httpClient: HttpClient,
    private sectionsService: SectionsService,
    protected lookupService: LookupService
  ) {
    this.data = {
      street1: '',
      street2: '',
      city: '',
      state: '',
      zipCode: '',
      countyOrRegion: '',
      psd_code: '',
      mailing: false,
    };

    this.isSmartyEnabled = environment.isSmartyEnabled;
    const smartyKey = environment.smartyEmbeddedKey;
    this.credentials = new SmartySDK.core.SharedCredentials(smartyKey);
  }

  ngOnDestroy(): void {
    if (this.typingTimer) {
      clearTimeout(this.typingTimer);
    }
  }

  ngOnInit(): void {
    const flow = this.sectionsService.getFlow();
    this.isParticipant =
      flow === 'participantEnrollment' || flow === 'referral';
    this.statesListDropdown = this.lookupService.getLookupValue('STATE');
    console.log('states', this.statesListDropdown);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['addressForm']) {
      const addressForm = changes['addressForm'].currentValue;
      const countyVal = addressForm.value.countyOrRegion;
      if (countyVal && !this.countyValidationService.isCountyValid(countyVal)) {
        this.countyValidationService.addCounty(countyVal);
        this.addressForm.patchValue({
          countyOrRegion: countyVal
        });
      }
      if (this.manualToggle?.nativeElement?.checked) {
        this.addressForm?.enable();
      } else {
        this.addressForm?.disable();
        this.addressForm?.get('mailing')?.enable();
      }
    }
  }

  getUsStreetClient(): any {
    if (!this.smartyStreetClient) {
      this.smartyStreetClient = SmartySDK.core.buildClient.usStreet(this.credentials);
    }

    return this.smartyStreetClient;
  }

  getAutocompleteClient(): any {
    if (!this.smartyAutoCompleteClient) {
      this.smartyAutoCompleteClient = SmartySDK.core.buildClient.usAutocompletePro(this.credentials);
    }

    return this.smartyAutoCompleteClient;
  }

  onSearch(value: string): void {
    if (this.typingTimer) {
      clearTimeout(this.typingTimer);
    }

    if (value && value.length > 3) {

      if (this.isSmartyEnabled) {
        this.typingTimer = setTimeout(() => {
          const client = this.getAutocompleteClient();

          const Lookup = SmartySDK.usAutocompletePro.Lookup;
          const lookup = new Lookup(value);
          lookup.maxResults = 10;

          return client.send(lookup)
            .then((response: any) => {
              if (response.result && response.result.length > 0) {
                const listOfOptions: { value: string; text: string }[] = [];
                response.result.forEach((result: any) => {
                  listOfOptions.push({
                    value: result,
                    text: `${result.streetLine}, ${result.city}, ${result.state} ${result.zipcode}`,
                  });
                });

                this.listOfOptions = listOfOptions;
              }
              return [];
            })
            .catch((err: any) => {
              console.error('Smarty autocomplete error:', err);
              return [];
            });
        }, 500);
      } else {
        this.httpClient
          .get(
            `https://api.addressy.com/Capture/Interactive/Find/v1.10/json3.ws?Key=${key}&Text=${value}&IsMiddleware=True&Origin=44.182,-84.506&Countries=US,CA&Limit=10&Language=&Bias=&Filters=&GeoFence=`,
          )
          .subscribe((data: any) => {
            const listOfOptions: { value: string; text: string }[] = [];
            data.Items.forEach((result: { Text: string }) => {
              listOfOptions.push({
                value: result.Text,
                text: result.Text,
              });
            });

            this.listOfOptions = listOfOptions;
          });
      }
    }
  }

  async validateAddress(value: any) {
    if (this.isSmartyEnabled) {
      const client = this.getUsStreetClient();
      const Lookup = SmartySDK.usStreet.Lookup;
      const lookup = new Lookup();
      lookup.street = value.streetLine;
      lookup.city = value.city;
      lookup.state = value.state;
      lookup.zipCode = value.zipcode;
      lookup.maxCandidates = 1;

      try {
        const response = await client.send(lookup);
        if (response?.lookups[0]?.result[0]) {
          const { deliveryLine1, components, metadata } = response.lookups[0].result[0];
          const zipCode = components.plus4Code ? `${components.zipCode}-${components.plus4Code}` : `${components.zipCode}`;
          const address = {
            AddressLine1: deliveryLine1,
            City: components.cityName,
            PostalCode: zipCode,
            State: components.state,
            County: metadata.countyName,
            CompleteAddress: `${deliveryLine1}, ${components.cityName}, ${components.state} ${zipCode}`,
          }
          this.extractSmartyAddress(address);
        }
      } catch (error: any) {
        console.error('Smarty usStreet error:', error);
        return;
      }
    } else {
      const body = {
        Key: `${key}`,
        Options: {
          Enhance: true,
        },
        Addresses: [
          {
            Address1: `${value}`,
            Address2: '',
            Country: 'US',
            Locality: '',
            PostalCode: '',
          },
        ],
      };
      const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      this.httpClient
        .post(
          `https://api.addressy.com/Cleansing/International/Batch/v1.00/json4.ws`,
          body,
          { headers: headers },
        )
        .subscribe((data: any) => {
          if (data && data[0].Matches) {
            this.extractAddress(data[0].Matches[0]);
          }
        });
    }
  }

  extractSmartyAddress(address: {
    AddressLine1: string,
    City: string,
    PostalCode: string,
    State: string,
    County: string,
    CompleteAddress: string;
  }) {
    const selectedAddress = {
      street1: address.AddressLine1,
      street2: '',
      zipCode: address.PostalCode,
      state: address.State,
      city: address.City,
      countyOrRegion: address.County,
    };

    if (this.manualToggle?.nativeElement?.checked) {
      this.addressForm.patchValue(selectedAddress);
    } else {
      this.addressForm.enable({ emitEvent: false });
      this.addressForm.patchValue(selectedAddress);
      this.addressForm.disable({ emitEvent: false });
      this.addressForm?.get('mailing')?.enable({ emitEvent: false });
    }

    this.listOfOptions = [];
    const countyName = address.County;
    if (!this.countyValidationService.isCountyValid(countyName)) {
      this.countyValidationService.addCounty(countyName);
    }
    this.fullAddress = address.CompleteAddress;
    if (this.onAddressSelection) {
      this.onAddressSelection.emit(this.fullAddress);
    }
    this.selectedValue = null;
  }

  extractAddress(address: {
    Address1: string;
    PostalCode: string;
    AdministrativeArea: string;
    Locality: string;
    SubAdministrativeArea: string;
    Address: string;
  }) {
    this.addressForm.patchValue({
      street1: address.Address1,
      street2: '',
      zipCode: address.PostalCode,
      state: address.AdministrativeArea,
      city: address.Locality,
      countyOrRegion: address.SubAdministrativeArea,
    });
    const countyName = address.SubAdministrativeArea;
    if (!this.countyValidationService.isCountyValid(countyName)) {
      this.countyValidationService.addCounty(countyName);
    }
    this.fullAddress = address.Address;
    if (this.onAddressSelection) {
      this.onAddressSelection.emit(this.fullAddress);
    }
    this.selectedValue = null;
  }

  toggleManualAddress(isChecked: boolean) {
    if (isChecked) {
      this.addressForm.enable();
    } else {
      this.addressForm.disable();
      this.addressForm.get('mailing')?.enable();
    }
  }
}
